<template>
  <HgModal v-model="internalModel" :closeButtonCssClass="$style.closeIcon" @close="closeModal">
    <template #default>
      <div :class="$style.modalContainer">
        <h2 :class="$style.heading">
          {{
            isNotGetMandateModal
              ? t('sellerLeads.notGotMandateModal.title')
              : t('sellerLeads.noSale.feedback.title')
          }}
        </h2>
        <p :class="$style.description">
          {{
            isNotGetMandateModal
              ? t('sellerLeads.notGotMandateModal.description')
              : t('sellerLeads.noSale.feedback.text')
          }}
        </p>

        <HgTextArea
          v-model="comment"
          name="not-mandate-comment"
          :label="t('sellerLeads.notGotMandateModal.comment')"
        />
      </div>
    </template>
    <template #footer>
      <HgButton
        ref="cancelButton"
        :theme="ButtonTheme.SecondaryBordered"
        :disabled="isLoading"
        @click="closeModal"
      >
        {{ t('sellerLeads.notGotMandateModal.cancelButtonText') }}
      </HgButton>
      <HgButton
        ref="submitButton"
        :theme="ButtonTheme.Primary"
        :disabled="isLoading || !formValid"
        @click="submitRequest"
        >{{ t('sellerLeads.notGotMandateModal.submitButtonText') }}</HgButton
      >
    </template>
  </HgModal>
</template>

<script setup lang="ts">
import { captureError } from '@smg-real-estate/browser-rum';
import { ButtonTheme, HgButton, HgModal, HgTextArea } from '@smg-real-estate/domus';
import { Status } from '@smg-real-estate/seller-leads-schema';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { updateStatus, type OptionalPayload } from '@/api/sellerLeads';
import { ModalIds, type Modal } from '@/components/ModalManager/types';
import { SelectedStatus, type DisplayableLead } from '@/models/sellerLead';
import { useStore } from 'vuex';

interface Props {
  modelValue: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const store = useStore();
const comment = ref<string>('');
const isLoading = ref<boolean>(false);

const currentModalId = computed<string>(() => {
  const modal = store.getters.getModals.find((item: Modal) => !!item.isOpen);
  return modal ? modal.id : '';
});
const isNotGetMandateModal = computed<boolean>(
  () => currentModalId.value === ModalIds.NotGotMandateModal,
);
const selectedStatus = computed<SelectedStatus>(() => store.getters.getSelectedStatus);

const rules = {
  comment: {
    $autoDirty: true,
    required,
  },
};
const v$ = useVuelidate(rules, { comment });
const formValid = computed(() => !v$.value.$invalid);

watch(
  () => props.modelValue,
  (val) => {
    if (!val) {
      resetData();
      v$.value.$reset();
    }
  },
);

const resetData = () => {
  comment.value = '';
  isLoading.value = false;
};

const closeModal = () => {
  const selectedLead: DisplayableLead = store.getters.getSelectedLead;
  store.commit('setSelectedStatus', {
    status: selectedLead.status,
    mandateState: selectedLead.mandateState ?? null,
  });
  store.dispatch('closeModal', { id: currentModalId.value });
};

const internalModel = computed({
  get() {
    return props.modelValue;
  },
  set(val: boolean) {
    emit('update:modelValue', val);
  },
});

const submitRequest = async () => {
  const valid = await v$.value.$validate();
  if (!valid) {
    return;
  }

  const payload: OptionalPayload = {
    commentNoMandateNotSold: comment.value,
    mandateState: selectedStatus.value?.mandateState || undefined,
  };

  try {
    const lead: DisplayableLead = store.getters.getSelectedLead;
    isLoading.value = true;
    const response = await updateStatus(lead.leadId, selectedStatus.value.status, payload);
    isLoading.value = false;
    if (response) {
      store.commit('setSelectedLead', {
        ...lead,
        ...(isNotGetMandateModal.value && { status: Status.LeadLost }),
        mandateState: payload.mandateState,
      });
    } else {
      store.commit('setHasStatusError', true);
    }
  } catch (exception) {
    store.commit('setHasStatusError', true);
    captureError(exception);
  } finally {
    closeModal();
  }
};
</script>

<style module lang="scss">
@import '@/assets/mixins';

.closeIcon {
  visibility: hidden;
}

.modalContainer {
  display: flex;
  flex-direction: column;
  row-gap: $space-200;
  margin-bottom: $space-100;
}

.errorMessage {
  @include body-3;
  @include vertical-margin;

  & {
    color: var(--validation-error-color);
  }
}

.heading {
  @include heading-2;
  @include vertical-margin;
}

.description {
  @include body-2;
}
</style>
